// src/utils/itemPicker.js
// P4-3 (30 Oct 2026, D-319) - the item list behind every item picker, in ONE place.
//
// WHY THIS FILE EXISTS
// Direct PO, Amend PO and MRF each built their own list and de-duplicated it by
// name, so the second of two same-name items could not be picked (dev: 16
// same-name pairs, 451 of 646 items share a code). Now:
//   - a picker's value is the canonical item_id - never the Firestore doc id,
//     never the name
//   - the list = own + shared "" items, skip only is_active === false, no
//     de-duplication, sorted by name then code
//   - every option shows name · code · unit, so two items can be told apart
//   - "already added" is decided by item_id, not by name
//   - an item that cannot be identified stays visible but cannot be picked
// Pure (no Firebase) -> covered by src/utils/itemPicker.test.js (Jest).

import { itemNameKey } from "./itemMatch";
import { findExactNameMatch } from "./poLines";

export const DUPLICATE_ID_REASON = "duplicate item id — fix in Item Master";
export const NO_ID_REASON = "no item id — fix in Item Master";

export const ambiguousNameMessage = (n) => `${n} items match this name — pick one from the list`;

const clean = (v) => String(v ?? "").trim();

/**
 * Own + shared "" items that are not disabled. A missing is_active = active.
 * Old docs keep their name in `name` and their category in `category`.
 */
export function activeCompanyItems(rawItems, companyId) {
  const cid = clean(companyId);
  const out = [];
  for (const raw of Array.isArray(rawItems) ? rawItems : []) {
    if (!raw) continue;
    if (raw.company_id !== cid && raw.company_id !== "") continue;
    if (raw.is_active === false) continue;
    const item_name = clean(raw.item_name) || clean(raw.name);
    if (!item_name) continue;
    out.push({ ...raw, item_name, item_category: raw.item_category || raw.category || "" });
  }
  return out;
}

const byNameThenCode = (a, b) =>
  a.item_name.localeCompare(b.item_name) || clean(a.item_code).localeCompare(clean(b.item_code));

/**
 * The options of one picker. An option with `_blocked` (the reason) is shown
 * disabled. Duplicates are counted BEFORE `allow`, so a category filter can
 * never hide the fact that an id is used twice.
 * @param {Object}   [opts]
 * @param {Function} [opts.allow]  the page's category test, (item) => boolean
 */
export function pickerItems(rawItems, companyId, { allow } = {}) {
  const active = activeCompanyItems(rawItems, companyId);
  const idCount = new Map();
  for (const it of active) {
    const id = clean(it.item_id);
    if (id) idCount.set(id, (idCount.get(id) || 0) + 1);
  }
  return active
    .filter((it) => !allow || allow(it))
    .map((it) => {
      const id = clean(it.item_id);
      if (!id) return { ...it, _blocked: NO_ID_REASON };
      return idCount.get(id) > 1 ? { ...it, _blocked: DUPLICATE_ID_REASON } : it;
    })
    .sort(byNameThenCode);
}

// "Ball Valve 32mm · MT-00488 · Nos"
export const itemOptionLabel = (item) =>
  [item?.item_name, item?.item_code, item?.unit].map(clean).filter(Boolean).join(" · ");

/**
 * Options matching what was typed ("250 mm" finds "250mm"). `_alreadyAdded`
 * compares item_id, so a same-name item with another id stays pickable.
 * Default limit 30: a short limit could cut off the second same-name item.
 */
export function searchPicker(list, queryStr, { takenIds = [], minChars = 2, limit = 30 } = {}) {
  if (clean(queryStr).length < minChars) return [];
  const q = itemNameKey(queryStr);
  const taken = new Set(takenIds.map(clean).filter(Boolean));
  return (Array.isArray(list) ? list : [])
    .filter((m) => itemNameKey(m.item_name).includes(q))
    .map((m) => ({ ...m, _alreadyAdded: taken.has(clean(m.item_id)) }))
    .slice(0, limit);
}

/**
 * What a fully typed name means.
 * @returns {{item: Object}|{ambiguous: number}|{blocked: string}|{}}
 *   item = link to it, ambiguous = N items have this name (stay unlinked),
 *   blocked = the one item cannot be picked, {} = no item has this name.
 */
export function typedNameLink(list, typed) {
  const key = itemNameKey(typed);
  if (key.length < 2) return {};
  const hits = (Array.isArray(list) ? list : []).filter((m) => itemNameKey(m.item_name) === key);
  if (hits.length === 0) return {};
  if (hits.length > 1) return { ambiguous: hits.length };
  if (hits[0]._blocked) return { blocked: hits[0]._blocked };
  return { item: findExactNameMatch(list, typed) };
}
